import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ResizeEvent } from "angular-resizable-element";
import { ToastrService } from "ngx-toastr";
import { Message } from "../../../models/message";
import { MessageControllerService } from "../../../services/message-controller/message-controller.service";
import { ModalService } from "../../utils/base_modal.component";
import { TranslatedBaseError } from "../TranslatedBaseError";
import { TranslateService } from "@ngx-translate/core";
import { RoleStatusMessage } from "../../../models/status-message";
import { MessageEditComponent } from "../roles/components/status/section/message-edit.component";

@Component({
  selector: "messages-table",
  templateUrl: "./messages-table.component.html",
  providers: [MessageControllerService, ModalService],
  styleUrls: ["./messages-table.component.scss"],
})
export class MessagesTableComponent extends TranslatedBaseError implements OnInit {
  @Input() public hideList: boolean = false;
  @Input() public useServiceDetailWrapper: boolean = true;
  @Input() public pageLocation: string = "Messages";
  public serviceId: string;
  public baseUrl: string;
  public messages: Message[] = [];
  public loadingServiceMessages: boolean = true;
  public filteredMessages: Message[] = [];
  public allMessages: Message[] = [];
  public searchValue: string = "";
  public searchTextFilterObject: any = {};
  // Sort variables
  public ascending: boolean = false;
  public resetSort: boolean = false;
  // Add/Edit variables
  public showEdit: boolean = false;
  public showAdd: boolean = false;
  public message: Message;
  public style: object = {};

  public editStatusMessage: boolean = false;
  public editMessage: boolean = false;
  public messageType: string = "MESSAGE";
  public statusType: string;
  public topLayer: string = "list";

  // Pagination
  public page: number = 1;
  public pageSize: number = 30;
  public totalPages: number = 1;
  public totalMessages: number = 0;
  public pageFilter: string = null;
  public sortKey: string = "name";
  public asc: string = "asc";
  public updatePageSender: any;

  public showAllmessages: boolean = false;
  public showServiceMessages: boolean = false;

  public statusMessages: RoleStatusMessage[];

  public showUsages: boolean = false;

  @ViewChild(MessageEditComponent) public messageEditComponent: MessageEditComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private messageControllerService: MessageControllerService,
    toastr: ToastrService,
    public translateService: TranslateService,
  ) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.searchTextFilterObject.name = "";
    this.route.params.subscribe((params) => {
      this.serviceId = params.serviceId;
      const routePath = this.route.snapshot.routeConfig?.path;
      if (routePath && routePath.includes("messagestable")) {
        this.baseUrl = `/services/${this.serviceId}/messagestable`;
      } else {
        this.baseUrl = `/services/${this.serviceId}/templates/messages`;
      }
    });
    this.initData();
  }

  public saveEdit() {
    this.messageEditComponent.submit();
  }

  public initData() {
    this.loadServiceMessages();
    this.loadAllMessages();
  }

  public updatePage(e: any) {
    if (e && e.trigger === "page") {
      this.page = e.page;
    }
    if (e && e.trigger === "search") {
      this.page = e.page;
      this.pageFilter = e.filter;
      this.filteredMessages = [...this.messages].filter((m) => {
        return m.name.includes(e.filter);
      });
    }
    if (e && e.trigger === "sort") {
      this.sortKey = e.sortKey;
      this.asc = e.asc;
    }
    this.loadAllMessages(this.page, this.pageSize, this.pageFilter, this.sortKey, this.asc, this.serviceId);
  }

  public pageNumber() {
    const pages = [];
    let index = 1;
    for (let i = 1; i <= this.totalPages; i++) {
      pages.push(index);
      index++;
    }
    return pages;
  }

  public searchTextChanged(value, emit: boolean = true) {
    this.searchValue = value;
    if (this.updatePageSender) {
      clearTimeout(this.updatePageSender);
    }
    this.updatePageSender = setTimeout(() => {
      this.searchPage();
    }, 2000);
  }

  public setPage(index: number) {
    this.updatePage({ page: index, trigger: "page" });
  }

  public searchPage() {
    const newSearchValue = this.searchValue.replace(/{/g, "%7B").replace(/}/g, "%7D");
    this.updatePage({ page: 1, trigger: "search", filter: newSearchValue });
  }

  public loadServiceMessages(serviceId: string = this.serviceId, page: number = this.page, size: number = this.pageSize, filter: string = this.pageFilter, sortKey: string = this.sortKey, asc: string = this.asc, channel: string = null) {
    this.messages = [];
    this.messageControllerService.getServiceMessagesListByPage(serviceId, (page - 1 > -1 ? page - 1 : 0), size, filter, sortKey, asc, channel).subscribe(
      (data) => {
        this.messages = data;
        this.loadingServiceMessages = false;
        if (this.messages) {
          if (this.statusMessages && this.statusMessages.length > 0) {
            for (let j = this.statusMessages.length - 1; j >= 0; j--) {
              for (let i = this.messages.length - 1; i >= 0; i--) {
                if (this.messages[i].id === this.statusMessages[j].message.id) {
                  this.messages.splice(i, 1).sort();
                }
              }
            }
          }
          this.messages.sort((a, b) => a.messageType !== this.messageType ? a.subject.toLowerCase() < b.subject.toLowerCase() ? -1 : 1 : 0);
        }
        if (!this.message && this.messages?.length > 0) {
          this.message = this.messages[0];
        }
        this.messages.sort((a, b) => {
          const nameA = a.subject.toLowerCase();
          const nameB = b.subject.toLowerCase();
          if (nameA < nameB) {
            return -1;
          }
        });
        this.filteredMessages = [...this.messages];
      },
      (error) => this.showErrorMessageWithTitle("Error HTTP Post Service ", error),
    );
  }

  public loadAllMessages(page: number = this.page, size: number = this.pageSize, filter: string = this.pageFilter, sortKey: string = this.sortKey, asc: string = this.asc, excludeMService: string = this.serviceId, channel: string = null) {
    this.allMessages = [];
    this.messageControllerService.getMessagesListByPage((page - 1 > -1 ? page - 1 : 0), size, filter, sortKey, asc, excludeMService, channel).subscribe(
      (data) => {
        this.allMessages = data.content;
        this.totalPages = data.totalPages;
        this.totalMessages = data.totalElements;
        if (data.totalElements > data.size) {
          this.page = data.number + 1;
        }
        if (this.allMessages) {
          if (this.statusMessages && this.statusMessages.length > 0) {
            for (let j = this.statusMessages.length - 1; j >= 0; j--) {
              for (let i = this.allMessages.length - 1; i >= 0; i--) {
                if (this.allMessages[i].id === this.statusMessages[j].message.id) {
                  this.allMessages.splice(i, 1).sort();
                }
              }
            }
          }
          this.allMessages.sort((a, b) => a.messageType !== this.messageType ? a.subject.toLowerCase() < b.subject.toLowerCase() ? -1 : 1 : 0);
        }
        this.allMessages.sort((a, b) => {
          const nameA = a.subject.toLowerCase();
          const nameB = b.subject.toLowerCase();
          if (nameA < nameB) {
            return -1;
          }
        });
      },
      (error) => this.showErrorMessageWithTitle("Error HTTP Post Service ", error),
    );
  }

  public openEditMessage(message) {
    this.editMessage = true;
    if (message) {
      this.message = message;
    } else {
      this.message = new Message();
      this.message.name = this.searchValue ? this.searchValue : "";
    }
  }

  public openMessageUsages(message) {
    this.showUsages = true;
    if (message) {
      this.message = message;
    } else {
      this.message = new Message();
      this.message.name = this.searchValue ? this.searchValue : "";
    }
  }

  public openAddStatus(message) {
    // console.log("--- openAddStatus", message);
    // this.editStatusMessage = true;
    this.message = message;
    // this.topLayer = "editStatusMessage";
  }

  public deleteMessage(message) {
    if (message) {
      this.messageControllerService.deleteMessage(message.id).subscribe(
        (data) => {
          this.responseSuccessful("Message deleted!");
          this.loadServiceMessages();
          this.loadAllMessages();
        },
        (error) => this.showErrorMessageWithTitle("Message delete error!", error),
      );
    }
  }

  public formatUsedNum(usages: any) {
    let roles = "";
    usages.forEach((i, index) => {
      if (index > 0) {
        roles += "\n";
      }
      roles += `${i.name}`;
    });
    return roles;
  }

  public resetListSearch() {
    this.searchValue = "";
    this.searchPage();
  }

  public cancelEdit() {
    this.editMessage = false;
    this.showUsages = false;
  }

  public messageWindowSubmitted(event) {
    if (event.save === true) {
      this.message = event.message;
      this.initData();
    } else if (event.save === false) {
      this.initData();
    }
  }

}
